import Icon, { IconName } from '@/components/atomic/icon'
import Text, { FontWeight, TextVariant } from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'

import styles from './order-book.module.css'
import {
  getBannerStyles,
  SummaryBannerContainer,
  SummaryMidContainer,
  SummarySideBannerButtonContainer,
} from './styles'
import { ActivePanelType, type OrderBookSummaryBannerProps } from './types'
import { getSummaryCountText, isPanelActive } from './utils'

const NA_TOOLTIP_TEXT =
  "'N/A' refers to quotes that are not actionable (usually due to order quantity being too small or too large)"

export function OrderBookSummaryBanner({
  summaryData,
  activePanel,
  selectSide,
}: OrderBookSummaryBannerProps) {
  const bannerStyles = getBannerStyles()
  const { background: bannerBackground, text: bannerText } = bannerStyles

  const bidsBannerStyle = isPanelActive(ActivePanelType.Bid, activePanel)
    ? bannerBackground.bidsActive
    : bannerBackground.neutral

  const bidsActiveTextStyle = isPanelActive(ActivePanelType.Bid, activePanel)
    ? bannerText.bidsActive
    : bannerText.neutral

  const asksBannerStyle = isPanelActive(ActivePanelType.Ask, activePanel)
    ? bannerBackground.asksActive
    : bannerBackground.neutral

  const asksActiveTextStyle = isPanelActive(ActivePanelType.Ask, activePanel)
    ? bannerText.asksActive
    : bannerText.neutral

  return (
    <SummaryBannerContainer>
      <SummarySideBannerButtonContainer
        onClick={() =>
          selectSide(
            activePanel === ActivePanelType.Bid
              ? ActivePanelType.Both
              : ActivePanelType.Bid,
          )
        }
        role="button"
        tabIndex={0}
        className={`${bidsBannerStyle} rounded-l ${styles['triangle-right']} ${styles.triangle} mr-[28px]`}
      >
        <div className="flex items-center gap-1">
          {summaryData.bidCount !== summaryData.bidEnabledCount && (
            <Tooltip simpleText={NA_TOOLTIP_TEXT}>
              <span>
                <Icon
                  iconName={IconName.InformationCircleIcon}
                  outline
                  size="small"
                />
              </span>
            </Tooltip>
          )}
          <Text
            className={`${bidsActiveTextStyle} mb-[1px]`}
            variant={TextVariant.BodyXS}
            fontWeight={FontWeight.Medium}
            omitWrapper
          >
            {getSummaryCountText(
              summaryData.bidEnabledCount,
              summaryData.bidCount,
              'Bids',
            )}
          </Text>
        </div>
        <Text
          className={`flex-row ${bidsActiveTextStyle} mb-[1px]`}
          variant={TextVariant.BodyXS}
          omitWrapper
        >
          {summaryData?.bidRange}
        </Text>
      </SummarySideBannerButtonContainer>
      <SummaryMidContainer
        onClick={() => selectSide(ActivePanelType.Both)}
        role="button"
        tabIndex={0}
        className={`${styles['triangle-left']} ${styles['triangle-right']} ${styles.triangle}`}
      >
        <Text
          className="flex-row my-1 mb-1.25"
          variant={TextVariant.BodyXS}
          omitWrapper
        >
          {summaryData?.marketMid}
        </Text>
      </SummaryMidContainer>
      <SummarySideBannerButtonContainer
        onClick={() =>
          selectSide(
            activePanel === ActivePanelType.Ask
              ? ActivePanelType.Both
              : ActivePanelType.Ask,
          )
        }
        role="button"
        tabIndex={0}
        className={`${asksBannerStyle} rounded-r ${styles['triangle-left']} ${styles.triangle} ml-[28px]`}
      >
        <Text
          className={`flex-row ${asksActiveTextStyle} mb-[1px]`}
          variant={TextVariant.BodyXS}
          omitWrapper
        >
          {summaryData?.askRange}
        </Text>
        <div className="flex items-center gap-1">
          <Text
            className={`${asksActiveTextStyle} mb-[1px]`}
            variant={TextVariant.BodyXS}
            fontWeight={FontWeight.Medium}
            omitWrapper
          >
            {getSummaryCountText(
              summaryData.askEnabledCount,
              summaryData.askCount,
              'Asks',
            )}
          </Text>
          {summaryData.askCount !== summaryData.askEnabledCount && (
            <Tooltip simpleText={NA_TOOLTIP_TEXT}>
              <span>
                <Icon
                  iconName={IconName.InformationCircleIcon}
                  outline
                  size="small"
                />
              </span>
            </Tooltip>
          )}
        </div>
      </SummarySideBannerButtonContainer>
    </SummaryBannerContainer>
  )
}
